import type { LensPeriod } from '~/types/leLens';

import { formatLensDayLabel } from './lensChart';
import { periodLabel } from './lensPeriodPresets';

/**
 * A polite screen-reader announcement channel shared by every Lens filter
 * control. Any component, including a cross-filter target that lives outside
 * this directory's dashboard filter bar, can call announceLensFilter without
 * needing a shared ancestor. LensLiveRegion is the one place that renders it,
 * so a screen reader hears exactly one announcement per change.
 */

export type LensAnnouncement = { message: string; id: number };

type Listener = (announcement: LensAnnouncement) => void;

const listeners = new Set<Listener>();
let sequence = 0;

export function announceLensFilter(message: string): void {
  if (!message) {
    return;
  }
  sequence += 1;
  const announcement: LensAnnouncement = { message, id: sequence };
  listeners.forEach((listener) => listener(announcement));
}

export function subscribeLensAnnouncer(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function cardWord(count: number): string {
  return `${count} card${count === 1 ? '' : 's'} updated`;
}

/** Covers ticking a value, "Only", select-all and clearing back to every value. */
export function describeSelectionAnnouncement(
  label: string,
  next: Array<string> | undefined,
  affectedCardCount: number,
): string {
  if (next === undefined) {
    return `Cleared the ${label} filter. ${cardWord(affectedCardCount)}.`;
  }
  if (next.length === 0) {
    return `Filtered ${label} to no values. ${cardWord(affectedCardCount)}.`;
  }
  if (next.length <= 3) {
    return `Filtered to ${next.join(', ')}. ${cardWord(affectedCardCount)}.`;
  }
  return `Filtered ${label} to ${next.length} values. ${cardWord(affectedCardCount)}.`;
}

/** Announced once a debounced measure range settles. */
export function describeRangeAnnouncement(
  label: string,
  range: { min?: number; max?: number },
  affectedCardCount: number,
): string {
  if (range.min === undefined && range.max === undefined) {
    return `Cleared the ${label} range. ${cardWord(affectedCardCount)}.`;
  }
  const from = range.min === undefined ? 'the minimum' : String(range.min);
  const to = range.max === undefined ? 'the maximum' : String(range.max);
  return `Filtered ${label} from ${from} to ${to}. ${cardWord(affectedCardCount)}.`;
}

export function describeClearAllAnnouncement(affectedCardCount: number): string {
  return `Cleared all filters. ${cardWord(affectedCardCount)}.`;
}

/** For a bar or table row cross-filter target once one is wired to this channel. */
export function describeCrossFilterAnnouncement(value: string, affectedCardCount: number): string {
  return `Filtered to ${value}. ${cardWord(affectedCardCount)}.`;
}

/** "Clear cross-filters" or Escape on the canvas: every mark selection goes, the dashboard filters do not. */
export function describeCrossFilterClearedAnnouncement(): string {
  return 'Cross-filter cleared. Showing all rows.';
}

export function describePeriodAnnouncement(
  from: string | undefined,
  to: string | undefined,
  datasetCount: number,
): string {
  // Spoken, so it says the date the way the page writes it rather than reading
  // an ISO string out loud.
  const spoken = (value: string) => formatLensDayLabel(value, true);
  const window =
    from && to
      ? `${spoken(from)} to ${spoken(to)}`
      : from
        ? `from ${spoken(from)}`
        : to
          ? `up to ${spoken(to)}`
          : 'the full period';
  return `Applied period ${window}. Re-running ${datasetCount} dataset${datasetCount === 1 ? '' : 's'}.`;
}

/** Describe the submitted window before its resolved query response arrives. */
export function describePeriodSelectionAnnouncement(period: LensPeriod, datasetCount: number): string {
  if (typeof period.window === 'object' && 'from' in period.window) {
    return describePeriodAnnouncement(period.window.from, period.window.to, datasetCount);
  }
  return `Applied period ${periodLabel(period)}. Re-running ${datasetCount} dataset${datasetCount === 1 ? '' : 's'}.`;
}
